/**
 * Textual Quality Panel Component
 *
 * DC-801: Displays textual quality analysis results:
 * - Lexical diversity (TTR, MTLD, HD-D)
 * - Readability (Flesch, SMOG, ARI, Gunning Fog)
 * - Topic coherence
 * - TF-IDF analysis
 */

import React, { useState, useRef, useEffect } from 'react';
import './textual-quality-panel.css';

type Severity = 'none' | 'low' | 'medium' | 'high';

interface LexicalDiversityResult {
  column: string;
  sample_size: number;
  ttr: { ttr: number; interpretation: string };
  mtld: { mtld: number; interpretation: string };
  hdd: { hdd: number; interpretation: string };
  overall_diversity: string;
  severity: Severity;
  recommendation: string;
}

interface ReadabilityResult {
  column: string;
  sample_size: number;
  avg_word_count: number;
  avg_sentence_count: number;
  flesch: { score: number; grade_level: number; interpretation: string };
  smog: { score: number; interpretation: string };
  ari: { score: number; grade_level: number; interpretation: string };
  gunning_fog: { score: number; interpretation: string };
  overall_grade_level: number;
  severity: Severity;
  recommendation: string;
}

interface TopicCoherenceResult {
  column: string;
  sample_size: number;
  num_topics: number;
  topics: Array<{
    topic_id: number;
    top_words: string[];
    weight: number;
    coherence_score: number;
  }>;
  avg_coherence: number;
  topic_diversity: number;
  interpretation: string;
  severity: Severity;
  recommendation: string;
}

interface TFIDFResult {
  column: string;
  document_count: number;
  vocabulary_stats: {
    unique_terms: number;
    total_terms: number;
    rare_terms_count: number;
  };
  top_terms: Array<{ term: string; tfidf: number }>;
  stopword_ratio: number;
  sparsity: number;
  interpretation: string;
  severity: Severity;
  recommendation: string;
}

interface TextualQualityResult {
  analyzed_at?: string;
  text_columns_detected: number;
  text_columns_analyzed: number;
  lexical_diversity?: {
    columns_analyzed: number;
    column_results: LexicalDiversityResult[];
    overall_severity: Severity;
    summary: string;
  };
  readability?: {
    columns_analyzed: number;
    expected_audience?: string;
    column_results: ReadabilityResult[];
    overall_severity: Severity;
    summary: string;
  };
  topic_coherence?: {
    columns_analyzed: number;
    column_results: TopicCoherenceResult[];
    overall_severity: Severity;
    summary: string;
  };
  tfidf?: {
    columns_analyzed: number;
    column_results: TFIDFResult[];
    overall_severity: Severity;
    summary: string;
  };
  overall_issues_found: number;
  overall_severity: Severity;
  summary: string;
}

interface TextualQualityPanelProps {
  journeyId?: string;
  results?: TextualQualityResult;
  onAnalysisComplete?: (result: TextualQualityResult) => void;
}

const TextualQualityPanel: React.FC<TextualQualityPanelProps> = ({
  journeyId,
  results: propResults,
  onAnalysisComplete
}) => {
  const [result, setResult] = useState<TextualQualityResult | null>(propResults || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'lexical' | 'readability' | 'topic' | 'tfidf'>('lexical');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (propResults) {
      setResult(propResults);
    }
  }, [propResults]);

  const handleAnalyze = async (file: File) => {
    try {
      setAnalyzing(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      if (journeyId) {
        formData.append('journey_id', journeyId);
      }

      const response = await fetch('/api/data-quality/textual/analyze', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to analyze textual quality');
      }

      setResult(data.result);
      onAnalysisComplete?.(data.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityClass = (severity: Severity): string => {
    return `textual-severity--${severity}`;
  };

  const toggleSection = (key: string) => {
    setExpandedSection(expandedSection === key ? null : key);
  };

  const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`;

  // Render lexical diversity section
  const renderLexicalDiversity = () => {
    const ld = result?.lexical_diversity;
    if (!ld || ld.column_results.length === 0) {
      return <div className="textual-empty">No lexical diversity analysis available</div>;
    }

    return (
      <div className="textual-section">
        <p className="textual-summary">{ld.summary}</p>
        {ld.column_results.map((col, idx) => (
          <div key={idx} className="textual-result-item">
            <div className="textual-result-header" onClick={() => toggleSection(`ld-${col.column}`)}>
              <span className="textual-result-name">{col.column}</span>
              <span className={`textual-badge ${getSeverityClass(col.severity)}`}>
                {col.overall_diversity}
              </span>
              <span className="textual-expand-icon">
                {expandedSection === `ld-${col.column}` ? '▼' : '▶'}
              </span>
            </div>
            {expandedSection === `ld-${col.column}` && (
              <div className="textual-result-details">
                <div className="textual-metrics">
                  <div className="textual-metric">
                    <span className="textual-metric-label">TTR</span>
                    <span className="textual-metric-value">{col.ttr.ttr.toFixed(3)}</span>
                    <span className="textual-metric-interp">{col.ttr.interpretation}</span>
                  </div>
                  <div className="textual-metric">
                    <span className="textual-metric-label">MTLD</span>
                    <span className="textual-metric-value">{col.mtld.mtld.toFixed(1)}</span>
                    <span className="textual-metric-interp">{col.mtld.interpretation}</span>
                  </div>
                  <div className="textual-metric">
                    <span className="textual-metric-label">HD-D</span>
                    <span className="textual-metric-value">{col.hdd.hdd.toFixed(3)}</span>
                    <span className="textual-metric-interp">{col.hdd.interpretation}</span>
                  </div>
                </div>
                <p className="textual-recommendation">{col.recommendation}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render readability section
  const renderReadability = () => {
    const rd = result?.readability;
    if (!rd || rd.column_results.length === 0) {
      return <div className="textual-empty">No readability analysis available</div>;
    }

    return (
      <div className="textual-section">
        <p className="textual-summary">{rd.summary}</p>
        {rd.expected_audience && (
          <p className="textual-config">Expected audience: {rd.expected_audience}</p>
        )}
        {rd.column_results.map((col, idx) => (
          <div key={idx} className="textual-result-item">
            <div className="textual-result-header" onClick={() => toggleSection(`rd-${col.column}`)}>
              <span className="textual-result-name">{col.column}</span>
              <span className={`textual-badge ${getSeverityClass(col.severity)}`}>
                Grade {col.overall_grade_level.toFixed(1)}
              </span>
              <span className="textual-expand-icon">
                {expandedSection === `rd-${col.column}` ? '▼' : '▶'}
              </span>
            </div>
            {expandedSection === `rd-${col.column}` && (
              <div className="textual-result-details">
                <div className="textual-metrics">
                  <div className="textual-metric">
                    <span className="textual-metric-label">Flesch</span>
                    <span className="textual-metric-value">{col.flesch.score.toFixed(1)}</span>
                    <span className="textual-metric-interp">{col.flesch.interpretation}</span>
                  </div>
                  <div className="textual-metric">
                    <span className="textual-metric-label">SMOG</span>
                    <span className="textual-metric-value">{col.smog.score.toFixed(1)}</span>
                    <span className="textual-metric-interp">{col.smog.interpretation}</span>
                  </div>
                  <div className="textual-metric">
                    <span className="textual-metric-label">ARI</span>
                    <span className="textual-metric-value">{col.ari.score.toFixed(1)}</span>
                    <span className="textual-metric-interp">{col.ari.interpretation}</span>
                  </div>
                  <div className="textual-metric">
                    <span className="textual-metric-label">Gunning Fog</span>
                    <span className="textual-metric-value">{col.gunning_fog.score.toFixed(1)}</span>
                    <span className="textual-metric-interp">{col.gunning_fog.interpretation}</span>
                  </div>
                </div>
                <p className="textual-recommendation">{col.recommendation}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render topic coherence section
  const renderTopicCoherence = () => {
    const tc = result?.topic_coherence;
    if (!tc || tc.column_results.length === 0) {
      return <div className="textual-empty">No topic coherence analysis available</div>;
    }

    return (
      <div className="textual-section">
        <p className="textual-summary">{tc.summary}</p>
        {tc.column_results.map((col, idx) => (
          <div key={idx} className="textual-result-item">
            <div className="textual-result-header" onClick={() => toggleSection(`tc-${col.column}`)}>
              <span className="textual-result-name">{col.column}</span>
              <span className={`textual-badge ${getSeverityClass(col.severity)}`}>
                {col.interpretation} ({col.num_topics} topics)
              </span>
              <span className="textual-expand-icon">
                {expandedSection === `tc-${col.column}` ? '▼' : '▶'}
              </span>
            </div>
            {expandedSection === `tc-${col.column}` && (
              <div className="textual-result-details">
                <div className="textual-stats">
                  <span>Avg Coherence: {col.avg_coherence.toFixed(3)}</span>
                  <span>Topic Diversity: {formatPercent(col.topic_diversity)}</span>
                </div>
                <div className="textual-topics">
                  <strong>Topics:</strong>
                  {col.topics.slice(0, 5).map((topic, tIdx) => (
                    <div key={tIdx} className="textual-topic">
                      <span className="textual-topic-id">Topic {topic.topic_id + 1}</span>
                      <span className="textual-topic-words">{topic.top_words.slice(0, 5).join(', ')}</span>
                    </div>
                  ))}
                </div>
                <p className="textual-recommendation">{col.recommendation}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render TF-IDF section
  const renderTFIDF = () => {
    const tf = result?.tfidf;
    if (!tf || tf.column_results.length === 0) {
      return <div className="textual-empty">No TF-IDF analysis available</div>;
    }

    return (
      <div className="textual-section">
        <p className="textual-summary">{tf.summary}</p>
        {tf.column_results.map((col, idx) => (
          <div key={idx} className="textual-result-item">
            <div className="textual-result-header" onClick={() => toggleSection(`tf-${col.column}`)}>
              <span className="textual-result-name">{col.column}</span>
              <span className={`textual-badge ${getSeverityClass(col.severity)}`}>
                {col.interpretation} ({formatPercent(col.sparsity)} sparse)
              </span>
              <span className="textual-expand-icon">
                {expandedSection === `tf-${col.column}` ? '▼' : '▶'}
              </span>
            </div>
            {expandedSection === `tf-${col.column}` && (
              <div className="textual-result-details">
                <div className="textual-stats">
                  <span>Unique Terms: {col.vocabulary_stats.unique_terms}</span>
                  <span>Stopword Ratio: {formatPercent(col.stopword_ratio)}</span>
                  <span>Rare Terms: {col.vocabulary_stats.rare_terms_count}</span>
                </div>
                {col.top_terms.length > 0 && (
                  <div className="textual-terms">
                    <strong>Top Terms:</strong>
                    <div className="textual-term-list">
                      {col.top_terms.slice(0, 10).map((term, tIdx) => (
                        <span key={tIdx} className="textual-term">
                          {term.term} <small>({term.tfidf.toFixed(1)})</small>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="textual-recommendation">{col.recommendation}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="textual-panel">
      {/* Header */}
      <div className="textual-panel__header">
        <div className="textual-panel__title">
          <h3>Textual Quality Analysis</h3>
          {result && (
            <span className={`textual-badge ${getSeverityClass(result.overall_severity)}`}>
              {result.overall_severity.toUpperCase()}
            </span>
          )}
        </div>
        <div className="textual-panel__actions">
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.tsv,.xlsx,.xls"
            onChange={(e) => e.target.files?.[0] && handleAnalyze(e.target.files[0])}
            style={{ display: 'none' }}
          />
          <button
            className="textual-btn textual-btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Analyze File'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="textual-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading */}
      {analyzing && (
        <div className="textual-loading">
          <div className="textual-spinner"></div>
          <span>Analyzing text quality (lexical, readability, topics, TF-IDF)...</span>
        </div>
      )}

      {/* Results */}
      {result && !analyzing && (
        <div className="textual-content">
          <div className="textual-overall-summary">
            <p>{result.summary}</p>
            <small>Analyzed {result.text_columns_analyzed} of {result.text_columns_detected} text columns</small>
          </div>

          <div className="textual-tabs">
            <button
              className={`textual-tab ${activeTab === 'lexical' ? 'textual-tab--active' : ''}`}
              onClick={() => setActiveTab('lexical')}
            >
              Lexical Diversity
              {result.lexical_diversity?.column_results.length ? (
                <span className="textual-tab-badge">{result.lexical_diversity.column_results.length}</span>
              ) : null}
            </button>
            <button
              className={`textual-tab ${activeTab === 'readability' ? 'textual-tab--active' : ''}`}
              onClick={() => setActiveTab('readability')}
            >
              Readability
              {result.readability?.column_results.length ? (
                <span className="textual-tab-badge">{result.readability.column_results.length}</span>
              ) : null}
            </button>
            <button
              className={`textual-tab ${activeTab === 'topic' ? 'textual-tab--active' : ''}`}
              onClick={() => setActiveTab('topic')}
            >
              Topic Coherence
              {result.topic_coherence?.column_results.length ? (
                <span className="textual-tab-badge">{result.topic_coherence.column_results.length}</span>
              ) : null}
            </button>
            <button
              className={`textual-tab ${activeTab === 'tfidf' ? 'textual-tab--active' : ''}`}
              onClick={() => setActiveTab('tfidf')}
            >
              TF-IDF
              {result.tfidf?.column_results.length ? (
                <span className="textual-tab-badge">{result.tfidf.column_results.length}</span>
              ) : null}
            </button>
          </div>

          <div className="textual-tab-content">
            {activeTab === 'lexical' && renderLexicalDiversity()}
            {activeTab === 'readability' && renderReadability()}
            {activeTab === 'topic' && renderTopicCoherence()}
            {activeTab === 'tfidf' && renderTFIDF()}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && !analyzing && !error && (
        <div className="textual-empty-state">
          <div className="textual-empty-icon">📝</div>
          <h4>Textual Quality Analysis</h4>
          <p>Upload a file to analyze:</p>
          <ul>
            <li><strong>Lexical Diversity</strong> - Vocabulary richness (TTR, MTLD, HD-D)</li>
            <li><strong>Readability</strong> - Grade level (Flesch, SMOG, ARI, Fog)</li>
            <li><strong>Topic Coherence</strong> - Theme consistency</li>
            <li><strong>TF-IDF</strong> - Term importance and sparsity</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default TextualQualityPanel;
